import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState, useRef } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";
import Cart, { generateCartItemsFrom } from "./Cart";

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

const Products = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(0);
  const [isloggedIn, setIsLoggedIn] = useState(false);
  const [cart, setCart] = useState([]);
  const [completeCartInfo, setCompleteCartInfo] = useState([]);

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Fetch products data and store it
  /**
   * Make API call to get the products list and store it to display the products
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on all available products
   *
   * API endpoint - "GET /products"
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "name": "iPhone XR",
   *          "category": "Phones",
   *          "cost": 100,
   *          "rating": 4,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "v4sLtEcMpzabRyfx"
   *      },
   *      {
   *          "name": "Basketball",
   *          "category": "Sports",
   *          "cost": 100,
   *          "rating": 5,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "upLK9JbQ4rMhTwt4"
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 500
   * {
   *      "success": false,
   *      "message": "Something went wrong. Check the backend console for more details"
   * }
   */

  const performAPICall = async () => {
    setLoading(true);
    const url = config.endpoint + "/products";
    console.log("Request sent to: " + url);
    try {
      const response = await axios.get(url).catch(() => {
        enqueueSnackbar("error", { variant: "error" });
      });
      setProductList(response.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const callFunctions = async () => {
      await performAPICall();
      if (localStorage.getItem("token")) {
        setIsLoggedIn(true);
        await fetchCart(localStorage.getItem("token"));
      } else setIsLoggedIn(false);
    };
    callFunctions();
  }, []);

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Implement search logic
  /**
   * Definition for search handler
   * This is the function that is called on adding new search keys
   *
   * @param {string} text
   *    Text user types in the search bar. To filter the displayed products based on this text.
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on filtered set of products
   *
   * API endpoint - "GET /products/search?value=<search-query>"
   *
   */
  const performSearch = async (text) => {
    setLoading(true);
    const url = config.endpoint + `/products/search?value=${text}`;
    console.log("Request sent to: " + url);
    try {
      const response = await axios.get(url).catch(() => {
        setNotFound(true);
      });
      if (response) {
        setNotFound(false);
        setProductList(response.data);
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Optimise API calls with debounce search implementation
  /**
   * Definition for debounce handler
   * With debounce, this is the function to be called whenever the user types text in the searchbar field
   *
   * @param {{ target: { value: string } }} event
   *    JS event object emitted from the search input field
   *
   * @param {NodeJS.Timeout} debounceTimeout
   *    Timer id set for the previous debounce call
   *
   */
  const debounceSearch = (event, debounceTimeout) => {
    const value = event.target.value;
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      performSearch(value);
    }, 500);
    setDebounceTimeout(timeout);
  };

  /**
   * Perform the API call to fetch the user's cart and return the response
   *
   * @param {string} token - Authentication token returned on login
   *
   * @returns { Array.<{ productId: string, qty: number }> | null }
   *    The response JSON object
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "productId": "KCRwjF7lN97HnEaY",
   *          "qty": 3
   *      },
   *      {
   *          "productId": "BW0jAAeDJmlZCF8i",
   *          "qty": 1
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 401
   * {
   *      "success": false,
   *      "message": "Protected route, Oauth2 Bearer token not found"
   * }
   */
  const fetchCart = async (token) => {
    if (!token) return;
    const url = config.endpoint + "/cart";
    try {
      // TODO: CRIO_TASK_MODULE_CART - Pass Bearer token inside "Authorization" header to get data from "GET /cart" API and return the response data
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCart(response.data);
    } catch (e) {
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch cart details. Check that the backend is running, reachable and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
      return null;
    }
  };

  useEffect(() => {
    setCompleteCartInfo(generateCartItemsFrom(cart, productList))
  }, [cart, productList]);

  // TODO: CRIO_TASK_MODULE_CART - Return if a product already exists in the cart
  /**
   * Return if a product already is present in the cart
   *
   * @param { Array.<{ productId: String, quantity: Number }> } items
   *    Array of objects with productId and quantity of products in cart
   * @param { String } productId
   *    Id of a product to be checked
   *
   * @returns { Boolean }
   *    Whether a product of given "productId" exists in the "items" array
   *
   */
  const isItemInCart = (items, productId) => {
    const itemsInCart = items.filter(item => item.productId === productId)
    return itemsInCart.length > 0;
  };

  /**
   * Perform the API call to add or update items in the user's cart and update local cart data to display the latest cart
   *
   * @param {string} token
   *    Authentication token returned on login
   * @param { Array.<{ productId: String, quantity: Number }> } items
   *    Array of objects with productId and quantity of products in cart
   * @param { Array.<Product> } products
   *    Array of objects with complete data on all available products
   * @param {string} productId
   *    ID of the product that is to be added or updated in cart
   * @param {number} qty
   *    How many of the product should be in the cart
   * @param {boolean} options
   *    If this function was triggered from the product card's "Add to Cart" button
   *
   * Example for successful response from backend:
   * HTTP 200 - Updated list of cart items
   * [
   *      {
   *          "productId": "KCRwjF7lN97HnEaY",
   *          "qty": 3
   *      },
   *      {
   *          "productId": "BW0jAAeDJmlZCF8i",
   *          "qty": 1
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 404 - On invalid productId
   * {
   *      "success": false,
   *      "message": "Product doesn't exist"
   * }
   */

  const addToCart = async (
    token,
    items,
    products,
    productId,
    qty,
    options = { preventDuplicate: false }
  ) => {
    if(options.preventDuplicate) {
      if(isItemInCart(items, productId)){
          enqueueSnackbar("Item already in cart.", {variant : "warning"})
          return null;
      }
  }

  var data = {
      "productId": productId,
      "qty": qty
  };

  var axiosConfig = {
      method: 'post',
      url: config.endpoint +"/cart",
      headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
      },
      data : data
  };

  axios(axiosConfig)
      .then(function (response) {
          if(options.handleAdd){
            enqueueSnackbar("Product added to cart", {variant:"success"})
          }
          else if (options.handleDelete){
            enqueueSnackbar("Product removed from cart", {variant:"success"})
          }
          setCart(response.data);
      })
      .catch(function (error) {
        enqueueSnackbar("Some error occured", {variant:"success"})
      });

};

  const handleAddToCart = (productId) => {
    if (!isloggedIn) {
      enqueueSnackbar("Please log in to add items to cart", {
        variant: "warning",
      });
      return null;
    } else {
      addToCart(localStorage.getItem("token"), cart, productList, productId, 1, {
        preventDuplicate: true,
        handleAdd: true
      });
    }
  };

  const handleQuantity = (productId, qty, options = { preventDuplicate: false, handleAdd: false, handleDelete: false }) => {
    addToCart(
      localStorage.getItem("token"),
      cart,
      productList,
      productId,
      qty,
      options
    );
  };

  return (
    <div>
      <Header>
        {/* TODO: CRIO_TASK_MODULE_PRODUCTS - Display search bar in the header for Products page */}
        <TextField
          className="search-desktop"
          size="small"
          fullWidth
          InputProps={{
            className: "search",
            endAdornment: (
              <InputAdornment position="end">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          placeholder="Search for items/categories"
          name="search"
          onChange={(event) => debounceSearch(event, debounceTimeout)}
        />
      </Header>

      {/* Search view for mobiles */}

      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
        onChange={(event) => debounceSearch(event, 1000)}
      />

      {isloggedIn ? (
        <Grid container>
          <Grid item className="product-grid" md={9} xs={12}>
            <Box className="hero">
              <p className="hero-heading">
                India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
                to your door step
              </p>
            </Box>
            {loading ? (
              <Box sx={{ height: 400 }}>
                <Grid
                  height="100%"
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <Grid item>
                    <CircularProgress />
                  </Grid>
                  <Grid item>Loading Products</Grid>
                </Grid>
              </Box>
            ) : notFound ? (
              <Box sx={{ height: 500 }}>
                <Grid
                  height="100%"
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <Grid item>
                    <SentimentDissatisfied />
                  </Grid>
                  <Grid item> No Products Found</Grid>
                </Grid>
              </Box>
            ) : (
              <Grid
                container
                alignItems="center"
                sx={{ pt: 4, px: 2 }}
                rowSpacing={{ xs: 1, sm: 2, md: 5 }}
                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              >
                {productList.map((item) => (
                  <Grid item xs={6} md={3} key={item._id}>
                    <ProductCard
                      product={item}
                      handleAddToCart={handleAddToCart}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
          <Grid item md={3} xs={12} sx={{ backgroundColor: "#E9F5E1" }}>
            <Cart
              products={productList}
              items={completeCartInfo}
              handleQuantity={handleQuantity}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container>
          <Grid item className="product-grid">
            <Box className="hero">
              <p className="hero-heading">
                India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
                to your door step
              </p>
            </Box>
            {loading ? (
              <Box sx={{ height: 400 }}>
                <Grid
                  height="100%"
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <Grid item>
                    <CircularProgress />
                  </Grid>
                  <Grid item>Loading Products</Grid>
                </Grid>
              </Box>
            ) : notFound ? (
              <Box sx={{ height: 500 }}>
                <Grid
                  height="100%"
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <Grid item>
                    <SentimentDissatisfied />
                  </Grid>
                  <Grid item> No Products Found</Grid>
                </Grid>
              </Box>
            ) : (
              <Grid
                container
                alignItems="center"
                sx={{ pt: 4, px: 2 }}
                rowSpacing={{ xs: 1, sm: 2, md: 5 }}
                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              >
                {productList.map((item) => (
                  <Grid item xs={6} md={3} key={item._id}>
                    <ProductCard product={item} handleAddToCart={handleAddToCart} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      {/* TODO: CRIO_TASK_MODULE_CART - Display the Cart component */}
      <Footer />
    </div>
  );
};

export default Products;
